import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { KeyValue } from "../../../libs/models/commonModel";
import {
  CommentReviewReportFilterReduxModel,
  ReportSearchFilterDateRangeModel,
} from "../../../libs/models/reportModel";
import { SurveyApi } from "../../../libs/services";
import { AppThunk } from "../../../store";

const getStartOfDay = () => new Date(new Date().setHours(0, 0, 0, 0));

const initialState: CommentReviewReportFilterReduxModel = {
  startDate: getStartOfDay(),
  endDate: new Date(),
  weekSelection: "",
  hierarchyId: 0,
  filters: {},
  questionnaires: [],
};

const reportSearchFilterSlice = createSlice({
  name: "reportSearchFilterState",
  initialState,
  reducers: {
    onDateValueSelection: (state, action: PayloadAction<string>) => {
      state.weekSelection = "";
    },
    onReportSearchFilterSubmit: (
      state,
      action: PayloadAction<CommentReviewReportFilterReduxModel>
    ) => {
      const actionData = action.payload;
      state.startDate = actionData.startDate ?? state.startDate;
      state.endDate = actionData.endDate ?? state.endDate;
      state.weekSelection = actionData.weekSelection ?? state.weekSelection;
      state.hierarchyId = actionData.hierarchyId ?? state.hierarchyId;
      state.filters = actionData.filters ?? state.filters;
    },
    questionnairesFetched: (state, action: PayloadAction<KeyValue[]>) => {
      state.questionnaires = action.payload;
    },
    dateRangeChanged: (
      state,
      action: PayloadAction<ReportSearchFilterDateRangeModel>
    ) => {
      state.startDate = action.payload.start ?? state.startDate;
      state.endDate = action.payload.end ?? state.endDate;
      state.weekSelection = "";
    },
    hierarchyChanged: (state, action: PayloadAction<number>) => {
      state.hierarchyId = action.payload ?? state.hierarchyId;
    },
    weekSelectionChanged: (state, action: PayloadAction<string>) => {
      const currentDate = new Date();
      const minDate =
        activeWeekDateRanges.find((item: any) => item.key === action.payload)
          ?.value ?? currentDate;
      state.weekSelection = action.payload ?? state.weekSelection;
      state.startDate = minDate;
      state.endDate = currentDate;
    },
    filtersChanged: (state, action: PayloadAction<{ [name: string]: any }>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
  },
});
export const {
  onReportSearchFilterSubmit,
  onDateValueSelection,
  questionnairesFetched,
  dateRangeChanged,
  hierarchyChanged,
  weekSelectionChanged,
  filtersChanged,
} = reportSearchFilterSlice.actions;
export default reportSearchFilterSlice.reducer;

export const fetchQuestionnaires =
  (): AppThunk =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async (dispatch, getState) => {
    const state = getState();
    if (
      state.reportSearchFilterState.questionnaires &&
      state.reportSearchFilterState.questionnaires.length > 0
    ) {
      return;
    }
    SurveyApi.getSurveyListForReports().then((questionnaires) => {
      if (questionnaires.responseCode === 200) {
        dispatch(questionnairesFetched(questionnaires.data ?? []));
      }
    });
  };

/**
 * On specific week item select in the filter
 * get the min (start) date
 */
const getWeekMinDate = (selectedWeek: string) => {
  let minDate = new Date(new Date().getTime() - 86400000); // "1D" one day
  // Normalize to the start of day (00:00:00.000) so the full day is included
  // in the date range, consistent with how FormDateRangePicker normalizes dates.
  minDate = new Date(minDate.setHours(0, 0, 0, 0));
  switch (selectedWeek) {
    case "1W": {
      minDate = new Date(minDate.setDate(minDate.getDate() - 6));
      break;
    }
    case "4W": {
      minDate = new Date(minDate.setDate(minDate.getDate() - 27));
      break;
    }
    case "1M": {
      minDate = new Date(minDate.setMonth(minDate.getMonth() - 1));
      break;
    }
    case "3M": {
      minDate = new Date(minDate.setMonth(minDate.getMonth() - 3));
      break;
    }
    case "6M": {
      minDate = new Date(minDate.setMonth(minDate.getMonth() - 6));
      break;
    }
    case "12M": {
      minDate = new Date(minDate.setMonth(minDate.getMonth() - 12));
      break;
    }
  }
  return minDate;
};

const activeWeekDateRanges = [
  {
    key: "1D",
    value: getWeekMinDate("1D"),
  },
  {
    key: "1W",
    value: getWeekMinDate("1W"),
  },
  {
    key: "4W",
    value: getWeekMinDate("4W"),
  },
  {
    key: "1M",
    value: getWeekMinDate("1M"),
  },
  {
    key: "3M",
    value: getWeekMinDate("3M"),
  },
  {
    key: "6M",
    value: getWeekMinDate("6M"),
  },
  {
    key: "12M",
    value: getWeekMinDate("12M"),
  },
];
